import {
  ChargeFunctionParameter,
  ChargeType,
  ChargeTypeInstance,
} from './charge-type.model';

/**
 * Enum representing the current state of an entity in the Accounting microservice.
 * This enum is used to track the lifecycle of an entity, providing information about its current usage status.
 */
export enum AccountingState {
  /**
   * The entity is in a draft state and not yet active
   */
  Draft = 'draft',

  /**
   * The entity is currently active and in use
   */
  Active = 'active',

  /**
   * The entity is currently inactive and not in use
   */
  Inactive = 'inactive',

  /**
   * The entity has been deleted
   */
  Deleted = 'deleted',
}

/**
 * Models the calculations to perform to compute a Client billable or Provider payable
 */
export interface PricingModel {
  /**
   * Display name of this pricing model
   * Must have a minimum length of 1
   */
  name: string;

  /**
   * List of line-item calculations to use
   * Must have at least one item
   */
  chargeTypes: ChargeTypeInstance[];

  /**
   * Unique identifier of this Pricing Model
   */
  pricingModelUUID: string;

  /**
   * Current state of an entity in the Accounting microservice
   */
  state: AccountingState;

  /**
   * First date, inclusive, on which this configuration entity may be used
   * Must match the pattern: ^\d{4}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$
   */
  effectiveStartDate: string;

  /**
   * Last date, inclusive, on which this configuration entity may be used;
   * the date after which this state will be set to 'inactive'.
   * If not set, this configuration is perpetual.
   * Must match the pattern: ^\d{4}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$
   */
  effectiveEndDate?: string;

  /**
   * ID of the last user to modify this entity; or SYSTEM
   * Must have a minimum length of 1
   */
  modifiedByUserID: string;

  /**
   * The last time this entity was modified
   */
  modifiedDateTimeUTC: string;

  /**
   * Application-wide unique ID for this entity instance
   */
  correlationUUID: string;
}

/**
 * Interface representing a pricing model that can be created.
 * This interface is used when creating a new pricing model, providing the necessary fields that must be specified.
 */
export interface CreatablePricingModel {
  /**
   * The display name of the pricing model.
   * It must have a minimum length of 1.
   */
  name: string;

  /**
   * An array of ChargeType objects representing the line-item calculations to use in this pricing model.
   * This array must contain at least one item.
   */
  chargeTypes: ChargeType[];
}

/**
 * Interface representing a pricing model that can be edited.
 * This interface is used when editing an existing pricing model, providing the necessary fields that must be specified.
 */
export interface EditablePricingModel {
  /**
   * The display name of the pricing model.
   * It must have a minimum length of 1.
   */
  name: string;

  /**
   * An array of ChargeType objects representing the line-item calculations to use in this pricing model.
   * This array must contain at least one item.
   */
  chargeTypes: ChargeType[];

  /**
   * The unique identifier of the pricing model.
   */
  pricingModelUUID: string;
}

/**
 * Interface representing a single charge applied to a Pricing Model.
 */
export interface PricingModelCharge {
  /**
   * Code that uniquely identifies the charge type within this Pricing Model.
   * This code must have a minimum length of 1.
   */
  chargeTypeBC: string;

  /**
   * Code referencing the Pricing Function to use for this charge.
   * This code must have a minimum length of 1.
   */
  pricingFunctionBC: string;

  /**
   * An array of parameter values to pass into the Pricing Functions within the Pricing Model.
   * This array must contain at least one item.
   */
  functionParameters: ChargeFunctionParameter[];

  /**
   * Business Code identifying the CPT code to use when billing charges based on this model.
   * This code must have a minimum length of 1.
   */
  cptCodeBC?: string;
}

/**
 * Instantiation of a Pricing Model into actual pricing when given the charges and pricing function parameter values
 */
export interface PricingModelInstance {
  /**
   * The unique identifier of the pricing model.
   */
  pricingModelUUID: string;

  /**
   * An array of PricingModelCharge objects representing the line-item calculations to use in this pricing model.
   * This array must contain at least one item.
   */
  charges: PricingModelCharge[];
}
